import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'utils/i18n';
import { Icons } from 'components/Icons';
import { getConfig } from 'utils/centralizedConfig';
import translations from './translations';
import { InvestorsStrengthsSection } from './components/InvestorsStrengthsSection';
import { InvestorsCompanyStructureSection } from './components/InvestorsCompanyStructureSection';
import { InvestorsResearchEconomySection } from './components/InvestorsResearchEconomySection';
import { WBox, FBox, CL, Dot, BG } from '../../homepage/components/ProfessionalHomepage/DiagramUtils';
import './Investors.css';

/** Labs site URL: local port (5001) or cloud BESPER_AI_LAB_URL */
function useBesperAiLabUrl() {
  const [url, setUrl] = useState(() => {
    if (typeof window === 'undefined') return null;
    const fromEnv = window._env_ && window._env_.BESPER_AI_LAB_URL;
    if (fromEnv) return fromEnv.replace(/\/+$/, '');
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://localhost:5001';
    }
    return null;
  });
  useEffect(() => {
    getConfig('BESPER_AI_LAB_URL').then((val) => {
      if (val) setUrl(String(val).replace(/\/+$/, ''));
    });
  }, []);
  return url;
}

/**
 * Goals (investors-regulators audience) page
 * Displays investor relations and regulatory information
 */
const Investors = () => {
  const { language } = useTranslation();
  const { lang } = useParams();
  const basePath = lang || language || 'en';
  const besperAiLabUrl = useBesperAiLabUrl();
  const [heroView, setHeroView] = useState('network');

  const t = (key, defaultValue = '') => {
    const pageTranslations = translations[language] || translations.en;
    const value = key.split('.').reduce((obj, k) => obj?.[k], pageTranslations);
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
    return defaultValue !== '' ? defaultValue : key;
  };

  const heroViews = [
    { id: 'tenant', label: 'Tenant view' },
    { id: 'network', label: 'Network view' },
    { id: 'collective', label: 'Collective view' },
  ];

  return (
    <div className="static-page investors-page">
      <div className="static-page-content investors-content" id="investors-content">
        <section className="investors-section investors-welcome" aria-labelledby="investors-welcome-heading">
          <div className="investors-hero-shell investors-hero-shell-v2 investors-hero-light">
            <div className="investors-hero-topbar">
              <span className="investors-welcome-eyebrow">{t('hero.badge', 'Investor Relations')}</span>
              <Link to={`/${basePath}/contact`} className="btn-investors">
                {t('invitation.cta', 'Contact us')}
                <Icons.ExternalLink size={18} />
              </Link>
            </div>

            <div className="investors-hero-mode-tabs" role="tablist" aria-label="Switch between tenant, network, and collective views">
              {heroViews.map((view) => {
                const active = heroView === view.id;
                return (
                  <button
                    key={view.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    aria-controls="investors-hero-panel"
                    className={`investors-hero-mode-tab ${active ? 'investors-hero-mode-tab-active' : ''}`}
                    onClick={() => setHeroView(view.id)}
                  >
                    {view.label}
                  </button>
                );
              })}
            </div>

            <div className="investors-hero-main investors-hero-main-v2">
              <div className="investors-hero-copy investors-hero-copy-v2">
                <span className="investors-section-overline" aria-hidden="true">01</span>
                <h1>{t('title', 'Goals')}</h1>
                <p className="investors-welcome-dek">
                  {t('description', 'Besper is a group of companies building secure European infrastructure with transparent, scope-based visibility for stakeholders.')}
                </p>
                <p className="investors-welcome-tagline">
                  {t('hero.tagline', 'Cloud or local hosting, shared ontology, and distributed zero-copy networking for controlled collaboration across markets.')}
                </p>

                <h2 id="investors-welcome-heading" className="investors-section-headline investors-hero-heading">
                  {heroView === 'tenant' && 'Tenant scale: isolated units with controlled sharing'}
                  {heroView === 'network' && 'Network scale: tenants connected by policy'}
                  {heroView === 'collective' && 'Collective scale: groups, investors, regulators'}
                </h2>

                {heroView === 'tenant' && (
                  <div className="investors-scale-stack" aria-label="Tenant model description">
                    <article className="investors-scale-card">
                      <p className="investors-scale-card-title">Isolated tenant unit</p>
                      <p className="investors-scale-card-body">One honeycomb represents a tenant with its own resources, identity, and policy perimeter.</p>
                    </article>
                    <article className="investors-scale-card">
                      <p className="investors-scale-card-title">Scoped sharing by owner</p>
                      <p className="investors-scale-card-body">Tenants choose what to expose, to whom, and under which conditions. Default posture remains isolated.</p>
                    </article>
                    <article className="investors-scale-card">
                      <p className="investors-scale-card-title">Query and be queried</p>
                      <p className="investors-scale-card-body">Connections are permissioned. Tenants can query peers and accept incoming queries with policy control.</p>
                    </article>
                  </div>
                )}

                {heroView === 'network' && (
                  <div className="investors-scale-stack" aria-label="Network model description">
                    <article className="investors-scale-card">
                      <p className="investors-scale-card-title">Cloud or local network</p>
                      <p className="investors-scale-card-body">Networks can run on-prem or on any cloud provider while keeping one policy model.</p>
                    </article>
                    <article className="investors-scale-card">
                      <p className="investors-scale-card-title">Distributed zero-copy links</p>
                      <p className="investors-scale-card-body">Cross-network communication happens without unnecessary copies while preserving scoped access boundaries.</p>
                    </article>
                    <article className="investors-scale-card">
                      <p className="investors-scale-card-title">Shared ontology language</p>
                      <p className="investors-scale-card-body">Objects mean the same across participants, enabling precise collaboration across companies and markets.</p>
                    </article>
                  </div>
                )}

                {heroView === 'collective' && (
                  <div className="investors-scale-stack" aria-label="Collective model description">
                    <article className="investors-scale-card">
                      <p className="investors-scale-card-title">Groups and subsidiaries</p>
                      <p className="investors-scale-card-body">Parent groups can connect subsidiary networks with scoped transparency and controlled coordination.</p>
                    </article>
                    <article className="investors-scale-card">
                      <p className="investors-scale-card-title">Investors and portfolio view</p>
                      <p className="investors-scale-card-body">Investors can monitor investee networks through permissioned data paths and ontology-aligned metrics.</p>
                    </article>
                    <article className="investors-scale-card">
                      <p className="investors-scale-card-title">Regulators and policy makers</p>
                      <p className="investors-scale-card-body">Regulators can query supervised entities with controlled scope and gain real-time digital twin insights.</p>
                    </article>
                  </div>
                )}

                <p className="investors-lead investors-hero-lead">
                  Because the B-esper platform uses a shared ontology, a "pig" object remains the same entity for restaurant, logistics, vendor, and farmer systems.
                  This semantic alignment supports cross-industry collaboration and faster, more precise economic action.
                </p>
              </div>

              <div
                id="investors-hero-panel"
                key={heroView}
                role="tabpanel"
                aria-live="polite"
                aria-atomic="true"
                className="investors-hero-visual investors-hero-visual-v2"
              >
                {heroView === 'tenant' && (
                  <>
                    <p className="investors-network-kicker">Tenant model</p>
                    <h3 id="investors-network-title" className="investors-network-title">Isolated tenant honeycomb</h3>
                    <p className="investors-network-intro">Each tenant is an isolated cell with resources and explicit share policies. Outbound and inbound queries are permissioned.</p>
                    <svg viewBox="0 0 380 300" className="investors-hero-svg" role="img" aria-label="Tenant with resources, data, query API, and policy isolation.">
                      <BG id="tenant-bg" w={380} h={300} mode="light" />
                      <FBox x={15} y={12} w={350} h={276} label="Tenant" mode="light" />
                      <WBox x={30} y={56} w={145} h={52} label="Resources" sub="services · bots" mode="light" />
                      <WBox x={205} y={56} w={145} h={52} label="Data" sub="objects · ontology" mode="light" />
                      <WBox x={30} y={180} w={145} h={52} label="Query API" sub="permissioned" mode="light" accent={true} />
                      <WBox x={205} y={180} w={145} h={52} label="Policy" sub="isolation · scope" mode="light" />
                      <WBox x={117} y={118} w={146} h={44} label="Process Interface" sub="automation" mode="light" />
                      <CL x1={102} y1={108} x2={102} y2={180} mode="light" />
                      <CL x1={277} y1={108} x2={277} y2={180} mode="light" />
                      <CL x1={175} y1={82} x2={205} y2={82} mode="light" />
                      <CL x1={175} y1={206} x2={205} y2={206} mode="light" />
                      <CL x1={190} y1={108} x2={190} y2={118} mode="light" />
                      <CL x1={190} y1={162} x2={190} y2={180} mode="light" />
                      <Dot cx={102} cy={108} mode="light" />
                      <Dot cx={277} cy={108} mode="light" />
                      <Dot cx={190} cy={118} mode="light" accent={true} />
                    </svg>
                    <ul className="investors-network-legend" aria-label="Tenant view summary">
                      <li>Isolated by default</li>
                      <li>Owner-controlled sharing scopes</li>
                      <li>Permissioned query access</li>
                    </ul>
                  </>
                )}

                {heroView === 'network' && (
                  <>
                    <p className="investors-network-kicker">Network model</p>
                    <h3 id="investors-network-title" className="investors-network-title">Federated network honeycombs</h3>
                    <p className="investors-network-intro">Tenants live in networks. Networks can be cloud or local. Networks link through distributed zero-copy with scoped data policies.</p>
                    <svg viewBox="0 0 500 300" className="investors-hero-svg" role="img" aria-label="Two connected networks with tenants, policy, services, and a zero-copy bridge.">
                      <BG id="network-bg" w={500} h={300} mode="light" />
                      <FBox x={12} y={12} w={195} h={276} label="Network A" mode="light" />
                      <FBox x={293} y={12} w={195} h={276} label="Network B" mode="light" />
                      <WBox x={26} y={56} w={168} h={48} label="Tenants" sub="isolated units" mode="light" />
                      <WBox x={26} y={128} w={168} h={48} label="Policy" sub="scoped access" mode="light" accent={true} />
                      <WBox x={26} y={200} w={168} h={48} label="Services" sub="K8s-native" mode="light" dim={true} />
                      <WBox x={307} y={56} w={168} h={48} label="Tenants" sub="isolated units" mode="light" />
                      <WBox x={307} y={128} w={168} h={48} label="Policy" sub="scoped access" mode="light" accent={true} />
                      <WBox x={307} y={200} w={168} h={48} label="Services" sub="K8s-native" mode="light" dim={true} />
                      <WBox x={213} y={102} w={74} h={96} label="Bridge" sub="zero-copy" mode="light" accent={true} />
                      <CL x1={207} y1={152} x2={213} y2={152} mode="light" delay={0.1} />
                      <CL x1={287} y1={152} x2={293} y2={152} mode="light" delay={0.2} />
                      <Dot cx={213} cy={152} mode="light" accent={true} />
                      <Dot cx={287} cy={152} mode="light" accent={true} />
                    </svg>
                    <ul className="investors-network-legend" aria-label="Network view summary">
                      <li>Distributed zero-copy exchange</li>
                      <li>Scoped access per policy</li>
                      <li>Same semantic language across networks</li>
                    </ul>
                  </>
                )}

                {heroView === 'collective' && (
                  <>
                    <p className="investors-network-kicker">Collective model</p>
                    <h3 id="investors-network-title" className="investors-network-title">Cross-market collective operations</h3>
                    <p className="investors-network-intro">Balanced visibility for groups, investors, and regulators over federated networks with explicit scope boundaries.</p>
                    <svg viewBox="0 0 500 300" className="investors-hero-svg" role="img" aria-label="Collective dashboard showing groups, investors, regulators and shared ontology over federated networks.">
                      <BG id="collective-bg" w={500} h={300} mode="light" />
                      <FBox x={10} y={12} w={152} h={276} label="Groups" mode="light" />
                      <FBox x={174} y={12} w={152} h={276} label="Networks" mode="light" accent={true} />
                      <FBox x={338} y={12} w={152} h={276} label="Regulators" mode="light" />
                      <WBox x={23} y={58} w={126} h={44} label="Subsidiaries" mode="light" dim={true} />
                      <WBox x={23} y={120} w={126} h={44} label="Portfolio" mode="light" dim={true} />
                      <WBox x={23} y={182} w={126} h={44} label="Reporting" mode="light" dim={true} />
                      <WBox x={187} y={58} w={126} h={44} label="Ontology" sub="shared" mode="light" accent={true} />
                      <WBox x={187} y={120} w={126} h={44} label="Federation" mode="light" />
                      <WBox x={187} y={182} w={126} h={44} label="Markets" mode="light" />
                      <WBox x={351} y={58} w={126} h={44} label="Supervision" mode="light" dim={true} />
                      <WBox x={351} y={120} w={126} h={44} label="Policy" mode="light" dim={true} />
                      <WBox x={351} y={182} w={126} h={44} label="Audit" mode="light" dim={true} />
                      <CL x1={162} y1={150} x2={174} y2={150} mode="light" delay={0.1} />
                      <CL x1={326} y1={150} x2={338} y2={150} mode="light" delay={0.2} />
                      <Dot cx={174} cy={150} mode="light" accent={true} />
                      <Dot cx={326} cy={150} mode="light" accent={true} />
                    </svg>
                    <ul className="investors-network-legend" aria-label="Collective view summary">
                      <li>Balanced triad governance</li>
                      <li>Investor portfolio intelligence</li>
                      <li>Regulatory precision insights</li>
                    </ul>
                  </>
                )}

                <p className="investors-welcome-focus-title" id="investors-welcome-focus">{t('welcome.focusTitle', "What you'll find")}</p>
                <ul className="investors-welcome-focus-list" aria-labelledby="investors-welcome-focus">
                  <li>{t('welcome.focus1', 'Group strategy & governance')}</li>
                  <li>{t('welcome.focus2', 'Cloud/local platform & infrastructure')}</li>
                  <li>{t('welcome.focus3', 'Distributed zero-copy network control')}</li>
                  <li>{t('welcome.focus4', 'Common ontology for market coordination')}</li>
                  <li>{t('welcome.focus5', 'Regulatory engagement & supervision')}</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <InvestorsCompanyStructureSection t={t} />
        <InvestorsStrengthsSection t={t} besperAiLabUrl={besperAiLabUrl} />
        <InvestorsResearchEconomySection t={t} />

        <section className="investors-section investors-section-alt investors-section-4-5">
          <div className="investors-bleed-inner investors-section-4-5-grid">
            <div className="investors-section-4-5-col investors-section-4-col">
              <span className="investors-section-overline" aria-hidden="true">05</span>
              <h2 className="investors-section-title">{t('european.title', 'European Focus & Cloud Orchestrator')}</h2>
              <p className="investors-section-subtitle">
                {t('european.intro', 'We want to make Europe more flexible and independent. B-esper acts as a European cloud orchestrator: the trusted layer between European companies and EU institutions.')}
              </p>
              <div className="investors-european-body">
                <p>
                  <strong>
                    {t(
                      'european.patriots',
                      'We are European patriots: we build this platform for Europe—its economy, society, and digital sovereignty—by conviction, not positioning.',
                    )}
                  </strong>
                </p>
                <p>{t('european.orchestrator', 'Our focus is being a cloud orchestrator. We enable hosting on Kubernetes on any cloud platform first.')}</p>
                <p>{t('european.smeEnterprise', 'Go-to-market starts with focused products such as B-esper Bot for SMEs; the architecture is intended to carry regulated enterprises as they consolidate workloads and data onto B-esper over time.')}</p>
                <p>{t('european.futureCloudProducts', 'In the future we will support any cloud platform product—for example serverless compute—so that complete architectures, not only SaaS customers, can be hosted on B-esper. Enterprises can run full workloads on our platform.')}</p>
                <p><strong>{t('european.atCost', 'We host at zero margin for these clients: infrastructure is offered at cost.')}</strong></p>
                <p>{t('european.migration', 'We offer products to migrate from international options—such as Microsoft Entra External ID—to European variants, strengthening European identity and compliance.')}</p>
                <p>{t('european.strengthenInfra', 'We aim to facilitate the strengthening of European infrastructure and digital sovereignty.')}</p>
                <p>{t('european.supervision', 'We invite investors and regulators to help us and provide supervision so that our platform remains compliant, transparent, and aligned with European values.')}</p>
              </div>
            </div>
            <div className="investors-section-4-5-col investors-section-5-col">
              <span className="investors-section-overline" aria-hidden="true">06</span>
              <div className="investors-invitation">
                <h2>{t('invitation.title', 'Invitation')}</h2>
                <p>{t('invitation.body', 'We welcome dialogue with investors and regulators. If you would like to engage, support our mission, or provide supervision, please get in touch.')}</p>
                <p>{t('invitation.body2', 'B-esper is building a European cloud orchestrator and investing in trustworthy, auditable AI. We are committed to transparency, compliance, and alignment with European values. Whether you are considering an investment, conducting due diligence, or representing a supervisory authority, we are happy to share our strategy, platform structure, and approach to regulation.')}</p>
                <p className="investors-invitation-ways-title">{t('invitation.waysTitle', 'How you can engage')}</p>
                <ul className="investors-invitation-ways">
                  <li>{t('invitation.way1', 'Investment and due diligence')}</li>
                  <li>{t('invitation.way2', 'Regulatory and supervisory dialogue')}</li>
                  <li>{t('invitation.way3', 'Partnership and collaboration')}</li>
                  <li>{t('invitation.way4', 'Strategy and compliance review')}</li>
                </ul>
              </div>
              <div className="investors-invitation-actions">
                <Link to={`/${basePath}/contact`} className="investors-link">
                  {t('invitation.cta', 'Contact us')}
                  <Icons.ExternalLink size={18} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Investors;
